import React, { useState, useEffect } from "react";
import styled, { keyframes, css } from "styled-components";
import { Modal } from "@material-ui/core";
import theme from "../../../assets/theme";
import { useSnackbar } from "notistack";
import useLoader from "../../../hooks/useLoader";
import { CustomInputField } from "../../common/FormInputs";
import {
  PrimaryCTAButton,
  PrimaryOutlinedCloseCTAButton,
} from "../../common/Buttons";
import { getErrorMessage } from "../../../helpers/functions";
import useBundle from "../../../hooks/useBundle";
import BundleService from "../../../services/BundleService";

export default function BundleName({
  openBundleModal,
  setOpenBundleModal,
  id,
  setRefreshDetails,
  setSuccess,
}) {
  const { enqueueSnackbar } = useSnackbar();
  const { setLoader } = useLoader();
  const [bundle, setBundle] = useState("");
  const { setBundleOpen } = useBundle();
  const [error, setError] = useState("");
  const [focused, setFocused] = useState(false);

  const addBundleName = async (value) => {
    try {
      const payload = { bundleName: value };
      setLoader({ state: true, message: "Creating Bundle..." });
      const res = await BundleService.addBundle(payload, id);
      if (res?.success === false) {
        enqueueSnackbar(res.message, { variant: "error" });
        return;
      }
      if (res) {
        setSuccess(true);
        setBundle("");
        setRefreshDetails(true);
      }
    } catch (error) {
      const message = getErrorMessage(error);
      enqueueSnackbar(message, { variant: "error" });
    } finally {
      setLoader({ state: false });
      setOpenBundleModal(false);
      setBundleOpen({ state: true, boolean: true });
    }
  };

  useEffect(() => {
    if (openBundleModal) setBundle("");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openBundleModal]);

  const handleChange = (event) => {
    const name = event.target.value;
    /* eslint-disable no-useless-escape */
    const regexPattern = /^[^\/\\=]+$/;
    if (!name || regexPattern.test(name)) {
      setError("");
    } else {
      setError(
        `Bundle name should not contain slashes ("/") and equal signs ("=")`,
      );
    }
    setBundle(name);
  };

  const isValid = bundle && !error;

  return (
    <Modal
      open={openBundleModal}
      onClose={() => setOpenBundleModal(false)}
      disableRestoreFocus={true}
    >
      <Overlay>
        <ModalCard role="presentation">
          {/* Decorative top accent */}
          <TopAccent />

          <ModalHeader>
            <IconCircle>
              <svg
                width="22"
                height="22"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M22 19a2 2 0 01-2 2H4a2 2 0 01-2-2V5a2 2 0 012-2h5l2 3h9a2 2 0 012 2z" />
                <line x1="12" y1="11" x2="12" y2="17" />
                <line x1="9" y1="14" x2="15" y2="14" />
              </svg>
            </IconCircle>
            <HeaderText>
              <ModalTitle>Create New Bundle</ModalTitle>
              <ModalSubtitle>
                Organize your documents into a named collection
              </ModalSubtitle>
            </HeaderText>
            <CloseBtn
              onClick={() => setOpenBundleModal(false)}
              aria-label="Close"
            >
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.5"
              >
                <line x1="18" y1="6" x2="6" y2="18" />
                <line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            </CloseBtn>
          </ModalHeader>

          <ModalDivider />

          <ModalBody>
            <FieldGroup focused={focused} hasError={!!error}>
              <FieldLabel>
                Bundle Name <Required>*</Required>
              </FieldLabel>
              <FieldHint>
                Choose a clear, descriptive name for easy identification
              </FieldHint>
              <InputWrapper focused={focused} hasError={!!error}>
                <InputIcon>
                  <svg
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                  >
                    <path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7" />
                    <path d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z" />
                  </svg>
                </InputIcon>
                <CustomInputField
                  variant="outlined"
                  error={!!error}
                  helperText={error}
                  required={true}
                  placeholder="e.g. Financial Documents Q1 2024"
                  value={bundle}
                  onChange={(e) => handleChange(e)}
                  onFocus={() => setFocused(true)}
                  onBlur={() => setFocused(false)}
                />
              </InputWrapper>
            </FieldGroup>

            {isValid && (
              <PreviewChip>
                <ChipDot />
                <ChipLabel>Bundle name preview</ChipLabel>
                <ChipText>"{bundle}"</ChipText>
              </PreviewChip>
            )}

            <TipBox>
              <svg
                width="14"
                height="14"
                viewBox="0 0 24 24"
                fill="none"
                stroke="#00838c"
                strokeWidth="2"
              >
                <circle cx="12" cy="12" r="10" />
                <line x1="12" y1="8" x2="12" y2="12" />
                <line x1="12" y1="16" x2="12.01" y2="16" />
              </svg>
              <TipText>
                Avoid using <strong>/</strong>, <strong>\</strong>, or{" "}
                <strong>=</strong> in bundle names.
              </TipText>
            </TipBox>
          </ModalBody>

          <ModalFooter>
            <PrimaryOutlinedCloseCTAButton
              style={{ flex: 1 }}
              onClick={() => setOpenBundleModal(false)}
            >
              Cancel
            </PrimaryOutlinedCloseCTAButton>
            <PrimaryCTAButton
              style={{ flex: 1 }}
              onClick={() => addBundleName(bundle)}
              disabled={!isValid}
            >
              Create Bundle
            </PrimaryCTAButton>
          </ModalFooter>
        </ModalCard>
      </Overlay>
    </Modal>
  );
}

/* ── Animations ── */
const fadeSlideIn = keyframes`
  from { opacity: 0; transform: translate(-50%, -46%) scale(0.96); }
  to   { opacity: 1; transform: translate(-50%, -50%) scale(1); }
`;

const chipIn = keyframes`
  from { opacity: 0; transform: translateY(6px); }
  to   { opacity: 1; transform: translateY(0); }
`;

/* ── Layout ── */
const Overlay = styled.div`
  position: fixed;
  inset: 0;
  -webkit-backdrop-filter: blur(4px);
`;

const ModalCard = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: calc(100% - 24px);
  max-width: 480px;
  background: #ffffff;
  border-radius: 24px;
  box-shadow:
    0 32px 80px rgba(0, 0, 0, 0.2),
    0 8px 24px rgba(0, 131, 140, 0.12);
  animation: ${fadeSlideIn} 0.28s cubic-bezier(0.34, 1.56, 0.64, 1);
  overflow: hidden;

  @media (max-width: 480px) {
    border-radius: 20px 20px 0 0;
    top: auto;
    bottom: 0;
    left: 0;
    transform: none;
    width: 100%;
    max-width: 100%;
    animation: none;
  }
`;

const TopAccent = styled.div`
  height: 4px;
  background: linear-gradient(90deg, #00838c 0%, #00b4c0 50%, #00838c 100%);
`;

const ModalHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 22px 22px 18px;
`;

const IconCircle = styled.div`
  width: 48px;
  height: 48px;
  border-radius: 14px;
  background: linear-gradient(135deg, #e6f7f8 0%, #b2e8eb 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  color: #00838c;
`;

const HeaderText = styled.div`
  flex: 1;
  min-width: 0;
`;

const ModalTitle = styled.h2`
  margin: 0;
  font-family: ${theme.fonts.primaryFontExtraBold};
  font-size: 18px;
  color: #293461;
  line-height: 1.2;

  @media (max-width: 380px) {
    font-size: 16px;
  }
`;

const ModalSubtitle = styled.p`
  margin: 4px 0 0;
  font-size: 12px;
  color: #64748b;
  font-family: ${theme.fonts.primaryFontRegular};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const CloseBtn = styled.button`
  width: 36px;
  height: 36px;
  border-radius: 10px;
  border: 1.5px solid #e2e8f0;
  background: #f8fafc;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #64748b;
  transition: all 0.2s ease;
  flex-shrink: 0;
  &:hover {
    background: #fee2e2;
    border-color: #fca5a5;
    color: #ef4444;
    transform: rotate(90deg);
  }
`;

const ModalDivider = styled.div`
  height: 1px;
  background: linear-gradient(
    90deg,
    transparent,
    #e2e8f0 30%,
    #e2e8f0 70%,
    transparent
  );
  margin: 0 22px;
`;

const ModalBody = styled.div`
  padding: 22px;
  display: flex;
  flex-direction: column;
  gap: 14px;
`;

const FieldGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const FieldLabel = styled.label`
  font-family: ${theme.fonts.primaryFontSemiBold};
  font-size: 13px;
  color: #334155;
  display: flex;
  align-items: center;
  gap: 2px;
`;

const Required = styled.span`
  color: #ef4444;
`;

const FieldHint = styled.p`
  margin: 0 0 6px;
  font-size: 12px;
  color: #94a3b8;
  font-family: ${theme.fonts.primaryFontRegular};
  line-height: 1.4;
`;

const InputWrapper = styled.div`
  position: relative;
  display: flex;
  align-items: center;

  /* Override MUI input to accommodate icon */
  .MuiOutlinedInput-root {
    padding-left: 36px !important;
    border-radius: 12px !important;
    transition: all 0.2s ease;
    ${({ focused }) =>
      focused &&
      css`
        box-shadow: 0 0 0 3px rgba(0, 131, 140, 0.12);
      `}
  }
`;

const InputIcon = styled.div`
  position: absolute;
  left: 12px;
  top: 50%;
  transform: translateY(-50%);
  color: #94a3b8;
  z-index: 1;
  pointer-events: none;
  display: flex;
`;

const PreviewChip = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 14px;
  background: linear-gradient(135deg, #f0fdfb 0%, #e6f7f8 100%);
  border: 1.5px solid #99f6e4;
  border-radius: 12px;
  animation: ${chipIn} 0.2s ease;
`;

const ChipDot = styled.div`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: #00838c;
  flex-shrink: 0;
`;

const ChipLabel = styled.span`
  font-size: 11px;
  color: #64748b;
  font-family: ${theme.fonts.primaryFontRegular};
  white-space: nowrap;
`;

const ChipText = styled.span`
  font-size: 13px;
  font-family: ${theme.fonts.primaryFontSemiBold};
  color: #0f766e;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const TipBox = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 8px;
  padding: 10px 14px;
  background: #f0fdfb;
  border: 1px solid #ccfbf1;
  border-radius: 10px;

  svg {
    flex-shrink: 0;
    margin-top: 1px;
  }
`;

const TipText = styled.p`
  margin: 0;
  font-size: 12px;
  color: #0f766e;
  font-family: ${theme.fonts.primaryFontRegular};
  line-height: 1.5;

  strong {
    font-family: ${theme.fonts.primaryFontSemiBold};
  }
`;

const ModalFooter = styled.div`
  display: flex;
  gap: 12px;
  padding: 0 22px 22px;

  @media (max-width: 380px) {
    flex-direction: column-reverse;
  }
`;
